import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getOgImageConfig, getOgImageUrl, getTwitterImageUrl } from "@/lib/og-images";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  fbAppId?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  robots?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  breadcrumbs?: Array<{ name: string; item: string }>;
  structuredData?: object;
  noindex?: boolean;
  nofollow?: boolean;
}

// Open Graph / Twitter images MUST be absolute URLs. A relative path trips
// Facebook's scraper ("Invalid URL" warning) and breaks some link previews.
// Any explicit ogImage/twitterImage prop is passed through here so a relative
// value can't reach the rendered tag. Already-absolute and pre-encoded values
// pass through untouched (no double-encoding of %20).
const SITE_ORIGIN = "https://frayze.ca";
const toAbsoluteImageUrl = (img?: string): string | undefined => {
  if (!img) return img;
  if (/^https?:\/\//i.test(img)) return img;
  return `${SITE_ORIGIN}${img.startsWith("/") ? "" : "/"}${img}`;
};

export const SEOHead = ({
  title,
  description,
  keywords,
  fbAppId,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageAlt,
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonical,
  robots,
  author,
  publishedTime,
  modifiedTime,
  breadcrumbs,
  structuredData,
  noindex = false,
  nofollow = false
}: SEOHeadProps) => {
  const location = useLocation();

  // Build robots directive
  const robotsDirective = robots ||
    `${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}, max-snippet:-1, max-image-preview:large`;

  // Helper function to strip query params and ensure trailing slash on URLs
  const ensureCleanCanonical = (url: string) => {
    if (!url) return url;

    // Strip query parameters and hash fragments for canonical URLs
    // This prevents ?package=, ?category=, ?tier= etc. from creating duplicate canonicals
    try {
      const parsed = new URL(url);
      url = parsed.origin + parsed.pathname;
    } catch {
      // If URL parsing fails (relative URL), strip manually
      url = url.split('?')[0].split('#')[0];
    }

    // Don't add slashes to file URLs/images (matching logic from generate-sitemap.js)
    if (url.match(/\.(xml|jpg|jpeg|png|gif|webp|svg|pdf|css|js)$/i)) {
      return url;
    }

    // Ensure trailing slash
    if (!url.endsWith('/')) {
      return url + '/';
    }

    return url;
  };

  // Get canonical URL: always clean (no query params, trailing slash)
  const getCanonicalUrl = () => {
    if (canonical) {
      return ensureCleanCanonical(canonical);
    }
    return ensureCleanCanonical(`https://frayze.ca${location.pathname}`);
  };
  const canonicalUrl = getCanonicalUrl();

  // Get dynamic OG images
  const currentPath = location.pathname;
  const ogImageUrl = toAbsoluteImageUrl(ogImage) || getOgImageUrl(currentPath);
  const twitterImageUrl = toAbsoluteImageUrl(twitterImage) || getTwitterImageUrl(currentPath);
  const ogImageConfig = getOgImageConfig(currentPath);
  const imageAlt = ogImageAlt || ogImageConfig.alt;

  // Default structured data for organization
  const defaultStructuredData = generateProfessionalServiceSchema();

  const finalStructuredData = structuredData || defaultStructuredData;

  // Generate Breadcrumb Schema if breadcrumbs are provided
  let breadcrumbSchema = {};
  if (breadcrumbs && breadcrumbs.length > 0) {
    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.item
      }))
    };
  }

  // Combine structured data
  // If structuredData is an array (graph), append breadcrumbSchema to it
  // If it's an object, we might need to wrap in @graph or just output script tags sequentially
  // For simplicity, let's allow rendering multiple script tags if needed, or prefer merging if possible.
  // Actually, SEOHead only renders one JSON-LD block currently.
  // Let's keep it simple: We will prioritize the passed structuredData, but if we have breadcrumbs, we can output a second script tag or merge if relevant.
  // However, the cleanest way is often to put them in a @graph if multiple.

  // Revised approach: We will render breadcrumb schema in a separate script tag if it exists, to avoid conflict with the generic structuredData object structure.


  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsDirective} />
      {author && <meta name="author" content={author} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* No global preconnects: fonts are self-hosted, and GHL widgets load late on
          specific pages only. Global preconnects to them get flagged unused by Lighthouse. */}

      {/* Hreflang Tags */}
      <link rel="alternate" hrefLang="en-CA" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph Tags */}
      {fbAppId && <meta property="fb:app_id" content={fbAppId} />}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      {/* JPEG, not WebP: FB/iMessage/LinkedIn scrapers don't decode WebP OG images
          and silently fall back to a bare link. Cards stay WebP for on-page use only. */}
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content="Frayze" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@FrayzeIncCanada" />
      <meta name="twitter:creator" content="@FrayzeIncCanada" />
      <meta name="twitter:title" content={twitterTitle || title} />
      <meta name="twitter:description" content={twitterDescription || description} />
      <meta name="twitter:image" content={twitterImageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Article specific tags */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>

      {/* Breadcrumb Schema */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.item
            }))
          })}
        </script>
      )}

      {/* Additional SEO Meta Tags: viewport is in index.html (needed before JS loads) */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="theme-color" content="#5f3af2" />

      {/* Favicon and Icons - These rely on public folder assets */}
    </Helmet>
  );
};

